/**
 * WebToNative Bridge — In-App Purchase / Subscription Service
 * 
 * Wraps window.WTN.inAppPurchase for native Android/iOS subscriptions.
 * Falls back gracefully when not running inside a WebToNative shell.
 * 
 * Usage:
 *   import { isWTNAvailable, purchaseSubscriptionWTN, listPurchasesWTN } from '@/services/webtonative';
 */

/**
 * Check if the app is running inside a WebToNative native shell.
 */
export function isWTNAvailable() {
  // window.WTN is the SDK object itself and now loads on every page (browser
  // or app) since the bridge script is always included. Only window.WTN.isNativeApp
  // (true only inside the actual WebToNative-wrapped Android/iOS app) tells us
  // whether native purchase APIs will actually do anything — without this check,
  // a plain web/PWA visitor would be routed into the WTN branch and hang until
  // the 120s timeout instead of going to Stripe.
  return typeof window !== 'undefined' && !!window.WTN && !!window.WTN.isNativeApp;
}

/**
 * Purchase a subscription via the native store (Google Play / App Store).
 *
 * Uses the documented WTN API:
 *   window.WTN.inAppPurchase({ productId, productType, isConsumable, callback })
 * 
 * basePlanId is NOT passed — it is not in the official WTN spec and is silently
 * ignored by the native bridge. Each Play Console product has exactly one base
 * plan, so Play Billing resolves it unambiguously from productId alone.
 *
 * accountToken is not yet supported by the current WTN Android builds
 * (availability date: 09/06/2026). It will be added when builds are regenerated.
 *
 * @param {string} productId - The Play Console product ID ('1m-bm' or '1-year-body-max-plan')
 * @returns {Promise<{ isSuccess: boolean, receiptData?: any, error?: string }>}
 */
export function purchaseSubscriptionWTN(productId) {
  return new Promise((resolve) => {
    if (!isWTNAvailable() || !window.WTN.inAppPurchase) {
      resolve({ isSuccess: false, error: 'WebToNative not available' });
      return;
    }

    // Timeout guard — if callback never fires within 120 s, resolve as failed
    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve({ isSuccess: false, error: 'Purchase timed out' });
      }
    }, 120000);

    try {
      window.WTN.inAppPurchase({
        productId: productId,
        productType: 'SUBS',
        isConsumable: false,
        callback: function(data) {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          resolve({
            isSuccess: !!data?.isSuccess,
            receiptData: data?.receiptData || null,
            transactionId: data?.transactionId || null,
            productId: data?.productId || productId,
            error: data?.isSuccess ? null : (data?.error || 'Purchase failed'),
          });
        }
      });
    } catch (err) {
      if (!settled) {
        settled = true;
        clearTimeout(timeoutId);
        resolve({ isSuccess: false, error: err.message || 'Purchase error' });
      }
    }
  });
}

/**
 * Ask the native bridge for a product's store price, when the build exposes it.
 * WTN has no documented product-details API yet, so this resolves null on current
 * builds and starts working by itself once one of these methods appears.
 */
export function getProductPriceWTN(productId) {
  return new Promise((resolve) => {
    const wtn = typeof window !== 'undefined' ? window.WTN : null;
    const api = wtn && (wtn.getProductDetails || wtn.getInAppPurchaseDetails || wtn.inAppPurchaseDetails);
    if (!isWTNAvailable() || typeof api !== 'function') {
      resolve(null);
      return;
    }

    let settled = false;
    const timeoutId = setTimeout(() => {
      if (!settled) { settled = true; resolve(null); }
    }, 8000);

    try {
      api.call(wtn, {
        productId,
        productType: 'SUBS',
        callback: function (data) {
          if (settled) return;
          settled = true;
          clearTimeout(timeoutId);
          const d = data?.productDetails || data?.product || data || {};
          const price = d.formattedPrice || d.localizedPrice || d.price || d.priceString || null;
          resolve(price ? String(price) : null);
        },
      });
    } catch (err) {
      if (!settled) { settled = true; clearTimeout(timeoutId); resolve(null); }
    }
  });
}

/**
 * Query all active purchases from the native store via the documented WTN API.
 * Uses window.WTN.getAllPurchases (official WTN restore/query API).
 *
 * Callback shape per WTN docs:
 *   data.isSuccess  — boolean
 *   data.purchaseData — Array of Google Play purchase objects, each containing
 *                       at minimum { purchaseToken, productId }
 *
 * @returns {Promise<{ isSuccess: boolean, purchases?: Array, error?: string }>}
 */
export function listPurchasesWTN() {
  return new Promise((resolve) => {
    if (!isWTNAvailable()) {
      resolve({ isSuccess: false, purchases: [], error: 'WebToNative not available' });
      return;
    }

    if (!window.WTN.getAllPurchases) {
      resolve({ isSuccess: false, purchases: [], error: 'getAllPurchases not available in this WTN build' });
      return;
    }

    const timeoutId = setTimeout(() => {
      resolve({ isSuccess: false, purchases: [], error: 'getAllPurchases timed out' });
    }, 15000);

    try {
      window.WTN.getAllPurchases({
        callback: function(data) {
          clearTimeout(timeoutId);
          if (data?.isSuccess) {
            const purchases = (data.purchaseData || []).map(p => ({
              purchaseToken: p.purchaseToken,
              productId: p.productId || p.itemId,
              orderId: p.orderId || null,
            }));
            resolve({ isSuccess: true, purchases });
          } else {
            resolve({ isSuccess: false, purchases: [], error: 'getAllPurchases returned failure' });
          }
        }
      });
    } catch (err) {
      clearTimeout(timeoutId);
      resolve({ isSuccess: false, purchases: [], error: err.message || 'getAllPurchases error' });
    }
  });
}
